import { dailyCallStore } from 'features/daily-call/model';
import { useEffect, useRef, useState } from 'react';
import { cn, logger, useMouseVisibility } from 'shared/lib';
import { TalkingMode } from './TalkingMode';
import { UserVideo, useWebcam } from 'features/recognize-emotions';
import { Captions } from 'widgets/captions';
import { Timer } from './Timer';
import { featureFlags } from 'shared/model';
import { CallEndModal } from 'features/call-end';
import {
  hotkeyDescriptions,
  useKeyboardBindings,
  useKeyboardFullScreenAndCaptionsBinding
} from '../lib';
import { ToolTip } from 'shared/ui';
import { tavusPersonaStore } from 'entities/tavus/persona';
import { Call, Video, VideoSlash } from 'iconsax-reactjs';
import { ToggleCaptions } from 'features/toggle-captions';
import { ScreenCast } from 'features/recognize-emotions/ui/ScreenCast';
import { LuMonitor, LuMonitorOff } from 'react-icons/lu';

type Props = {
  onCallEnd: () => void;
};

export const TavusControlBar = ({ onCallEnd }: Props) => {
  const {
    callObject,
    isRecordingMic,
    startMic,
    stopMic,
    messages,
    withCaptions
  } = dailyCallStore((state) => ({
    callObject: state.callObject,
    isRecordingMic: state.isRecordingMic,
    startMic: state.startMic,
    stopMic: state.stopMic,
    messages: state.messages,
    withCaptions: state.withCaptions
  }));

  const timerIsEnable = tavusPersonaStore(
    (state) => !!state.currentPersona?.timer_config
  );

  const { toggleCaptions } = dailyCallStore();
  const [open, setOpen] = useState(false);

  const { startWebcam, stopWebcam, isWebcam, webcamLoading } = useWebcam();
  const [isScreencast, setIsScreencast] = useState(false);
  const [screencastStream, setScreencastStream] = useState<MediaStream | null>(
    null
  );

  const webcamRef = useRef<HTMLVideoElement>(null);
  const screencastRef = useRef<HTMLVideoElement>(null);

  // @ts-ignore
  useKeyboardFullScreenAndCaptionsBinding({ toggleCaptions });

  const isVisible = useMouseVisibility();

  const toggleMic = () => {
    isRecordingMic ? stopMic() : startMic();
  };

  const stopScreencast = async () => {
    if (screencastRef.current?.srcObject) {
      (screencastRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach((track) => track.stop());
      screencastRef.current.srcObject = null;
    }
    setScreencastStream(null);
    setIsScreencast(false);
  };

  const startScreencast = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      alert('Your browser does not support screen capture.');
      return;
    }

    try {
      stopWebcam();

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 60 },
        audio: false
      });

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        return;
      }

      await callObject?.setInputDevicesAsync({ videoSource: videoTrack });
      setScreencastStream(stream);
      setIsScreencast(true);
    } catch (err) {
      logger.error('Failed to start screencast:', err);
      setIsScreencast(false);
    }
  };

  const startWebcamHandler = async () => {
    if (isScreencast) {
      await stopScreencast();
    }

    if (webcamRef.current) {
      await startWebcam(webcamRef.current);
    }

    setIsScreencast(false);
  };

  const toggleVideo = async () => {
    if (isWebcam) {
      stopWebcam();
    } else {
      await startWebcamHandler();
    }
  };

  useKeyboardBindings({
    toggleMic,
    toggleWebcam: toggleVideo,
    setOpen
  });

  useEffect(() => {
    if (callObject) {
      callObject.setLocalAudio(isRecordingMic);
    }
  }, [isRecordingMic, callObject]);

  useEffect(() => {
    if (callObject) {
      callObject.setLocalVideo(isWebcam || isScreencast);
    }
  }, [isWebcam, isScreencast]);

  useEffect(() => {
    if (screencastRef.current && isScreencast && screencastStream) {
      const video = screencastRef.current;
      video.srcObject = screencastStream;
      video.onloadedmetadata = () => {
        video
          .play()
          // @ts-ignore
          .catch((err) => logger.warn('Autoplay screencast failed', err));
      };
    }
  }, [isScreencast, screencastRef.current, screencastStream]);

  useEffect(() => {
    return () => {
      stopWebcam();
      stopScreencast();
    };
  }, []);

  return (
    <div className="ag-text-white ag-absolute ag-bottom-0 ag-left-0 ag-right-0 ag-h-full ag-flex ag-flex-col ag-justify-between">
      <div className="ag-flex ag-flex-col ag-items-center ag-justify-end ag-h-full ag-relative pb-4">
        <div className="ag-pb-0.5 ag-flex ag-flex-col sm:ag-flex-row ag-items-start sm:ag-items-end ag-justify-between ag-gap-4 ag-w-full ag-px-4 sm:ag-px-8 ag-mb-4">
          {isScreencast ? (
            <ScreenCast ref={screencastRef} />
          ) : (
            <UserVideo ref={webcamRef} />
          )}
          <div
            className={`ag-absolute ag-left-0 ag-duration-300 ag-right-0 ${
              isVisible ? 'ag-bottom-32' : 'ag-bottom-5'
            }`}
          >
            <Captions
              withCaptions={withCaptions}
              messages={messages}
              isChatScreen
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          'min-h-1/5 ag-w-full ag-absolute ag-duration-300 ag-transition-all ag-flex-col ag-justify-center ag-items-center ag-text-center',
          isVisible ? 'ag-bottom-10' : '-ag-bottom-full'
        )}
      >
        <div className="ag-flex ag-gap-4 ag-py-4 ag-px-6 ag-items-center ag-justify-center">
          <button
            onClick={toggleVideo}
            disabled={webcamLoading}
            className="ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
          >
            {isWebcam ? (
              <Video size="24" color="currentColor" />
            ) : (
              <VideoSlash size="24" color="#DC2626" />
            )}
            <ToolTip
              topClass="-ag-top-10"
              text={hotkeyDescriptions.toggleWebcam}
            />
          </button>

          <button
            onClick={isScreencast ? stopScreencast : startScreencast}
            className="ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
          >
            {isScreencast ? (
              <LuMonitor size="24" color="currentColor" />
            ) : (
              <LuMonitorOff size="24" color="#DC2626" />
            )}
            <ToolTip topClass="-ag-top-10" text="Toggle Screencast" />
          </button>

          <ToggleCaptions
            withCaptions={withCaptions}
            toggleCaptions={toggleCaptions}
          />

          <div className="group ag-relative">
            <TalkingMode
              startMicrophone={startMic}
              stopMicrophone={stopMic}
              isRecordingMic={isRecordingMic}
            />
            <ToolTip
              className="-ag-top-10"
              text={hotkeyDescriptions.toggleMic}
            />
          </div>

          <button
            onClick={onCallEnd}
            className="ag-h-10 ag-w-10 hover:ag-opacity-80 ag-duration-300 ag-bg-button-red ag-text-white ag-py-2 ag-rounded-full ag-flex ag-flex-row ag-justify-center ag-items-center"
          >
            <Call color="currentColor" size="24" />
            <ToolTip className="-ag-top-10" text={hotkeyDescriptions.endCall} />
          </button>
        </div>

        <div className="ag-mx-auto">
          {timerIsEnable && featureFlags.conversationTimer && <Timer />}
        </div>
      </div>

      <CallEndModal open={open} onCallEnd={onCallEnd} setOpen={setOpen} />
    </div>
  );
};
